import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { jwtDecode } from 'jwt-decode'
import Constant from '@/constants/Constant'
import { usePermissionStore } from '@/stores/permission-store'
import { useGlobalStore } from '@/stores/global-store'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

function isTokenValid(token: string): boolean {
  try {
    const decoded: any = jwtDecode(token)
    return decoded.exp * 1000 > Date.now()
  } catch (e) {
    return false
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'vehicle-trackings' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: 'customs-declarations',
        children: [
          {
            name: 'customs-declarations',
            path: '',
            component: () => import('@/pages/customs-declaration/CustomsDeclarationPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'customs-declaration-create',
            path: 'new',
            component: () => import('@/pages/customs-declaration/CustomsDeclarationStorePage.vue'),
            meta: { requiresAuth: true, resource: 'customs-declarations', action: 'create' },
          },
          {
            name: 'customs-declaration-edit',
            path: ':id/edit',
            component: () => import('@/pages/customs-declaration/CustomsDeclarationStorePage.vue'),
            props: true,
            meta: { requiresAuth: true, resource: 'customs-declarations', action: 'update' },
          },
        ],
      },
      {
        path: 'vehicle-trackings',
        children: [
          {
            name: 'vehicle-trackings',
            path: '',
            component: () => import('@/pages/vehicle-tracking/VehicleTrackingPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'new',
            name: 'vehicle-tracking-create',
            component: () => import('@/pages/vehicle-tracking/VehicleTrackingStorePage.vue'),
            meta: { requiresAuth: true, resource: 'vehicle-trackings', action: 'create' },
          },
          {
            path: ':id',
            name: 'vehicle-tracking-detail',
            component: () => import('@/pages/vehicle-tracking/VehicleTrackingStorePage.vue'),
            props: true,
            meta: { requiresAuth: true, resource: 'vehicle-trackings', action: 'read' },
          },
          {
            path: ':id/edit',
            name: 'vehicle-tracking-edit',
            component: () => import('@/pages/vehicle-tracking/VehicleTrackingStorePage.vue'),
            props: true,
            meta: { requiresAuth: true, resource: 'vehicle-trackings', action: 'update' },
          },
        ],
      },
      {
        path: 'completed-vehicle-trackings',
        name: 'completed-vehicle-trackings',
        component: () => import('@/pages/vehicle-tracking/CompletedVehicleTrackingPage.vue'),
      },
      {
        path: 'additional-cost-requests',
        children: [
          {
            name: 'additional-cost-requests',
            path: '',
            component: () => import('@/pages/additional-cost-request/AdditionalCostRequestPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        path: 'debts',
        children: [
          {
            name: 'debts',
            path: '',
            component: () => import('@/pages/debt/DebtPage.vue'),
          },
        ],
      },
      {
        path: 'supplier-payables',
        children: [
          {
            name: 'supplier-payables',
            path: '',
            component: () => import('@/pages/supplier-payable/SupplierPayablePage.vue'),
          },
        ],
      },
      {
        name: 'user-guide',
        path: 'user-guide',
        component: () => import('@/pages/user-guide/UserGuidePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('@/pages/users-v2/UsersPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'error-logs',
        path: 'error-logs',
        component: () => import('@/pages/system/ErrorLogPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'job-execution-logs',
        path: 'job-execution-logs',
        component: () => import('@/pages/system/JobExecutionLogPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'system-configs',
        path: 'system-configs',
        component: () => import('@/pages/system/SystemConfigPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'import-export-logs',
        path: 'import-export-logs',
        component: () => import('@/pages/system/ImportExportLogPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'vehicle-tracking-column-configs',
        path: 'vehicle-tracking-column-configs',
        component: () => import('@/pages/system/VehicleTrackingColumnConfigPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'debt-column-configs',
        path: 'debt-column-configs',
        component: () => import('@/pages/system/DebtColumnConfigPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'supplier-payable-column-configs',
        path: 'supplier-payable-column-configs',
        component: () => import('@/pages/system/SupplierPayableColumnConfigPage.vue'),
        meta: { requiresAuth: true, usernames: ['super_admin'] },
      },
      {
        name: 'permission-matrix',
        path: 'permission-matrix',
        component: () => import('@/pages/permission-matrix/PermissionMatrixPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'permission-data',
        path: 'permission-data',
        component: () => import('@/pages/permission-data/PermissionDataPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'debits',
        path: 'debits',
        component: () => import('@/pages/debit/DebitPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'bookers',
        path: 'bookers',
        component: () => import('@/pages/booker/BookerPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'vehicle-types',
        path: 'vehicle-types',
        component: () => import('@/pages/vehicle-type/VehicleTypePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'declaration-statuses',
        path: 'declaration-statuses',
        component: () => import('@/pages/declaration-status/DeclarationStatusPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'vehicle-statuses',
        path: 'vehicle-statuses',
        component: () => import('@/pages/vehicle-status/VehicleStatusPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'tq-vehicle-statuses',
        path: 'tq-vehicle-statuses',
        component: () => import('@/pages/tq-vehicle-status/TqVehicleStatusPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'vn-vehicle-statuses',
        path: 'vn-vehicle-statuses',
        component: () => import('@/pages/vn-vehicle-status/VnVehicleStatusPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'declaration-customers',
        path: 'declaration-customers',
        component: () => import('@/pages/declaration-customer/DeclarationCustomerPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'logistics-services',
        path: 'logistics-services',
        component: () => import('@/pages/logistics-service/LogisticsServicePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'declaration-agencies',
        path: 'declaration-agencies',
        component: () => import('@/pages/declaration-agency/DeclarationAgencyPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'customs-declaration-services',
        path: 'customs-declaration-services',
        component: () => import('@/pages/customs-declaration-services/CustomsDeclarationServicesPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'suppliers',
        path: 'suppliers',
        component: () => import('@/pages/supplier/SupplierPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'departments',
        path: 'departments',
        component: () => import('@/pages/department/DepartmentPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'border-gates',
        path: 'border-gates',
        component: () => import('@/pages/border-gate/BorderGatePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'actual-locations',
        path: 'actual-locations',
        component: () => import('@/pages/actual-location/ActualLocationPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'billing-locations',
        path: 'billing-locations',
        component: () => import('@/pages/billing-location/BillingLocationPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'fee-groups',
        path: 'fee-groups',
        component: () => import('@/pages/fee-group/FeeGroupPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'fees',
        path: 'fees',
        component: () => import('@/pages/fee/FeePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'af-input-fees',
        path: 'af-input-fees',
        children: [
          {
            name: 'input-transportation-fee',
            path: 'input-transportation-fee',
            component: () => import('@/pages/af-input-fee/InputTransportationFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'input-drop-point-fee',
            path: 'input-drop-point-fee',
            component: () => import('@/pages/af-input-fee/InputDropPointFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'input-detention-fee',
            path: 'input-detention-fee',
            component: () => import('@/pages/af-input-fee/InputDetentionFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'input-border-procedure-fee',
            path: 'input-border-procedure-fee',
            component: () => import('@/pages/af-input-fee/InputBorderProcedureFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'input-icd-fee',
            path: 'input-icd-fee',
            component: () => import('@/pages/af-input-fee/InputIcdFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'input-transportation-fee-history',
            path: 'input-transportation-fee-history',
            component: () => import('@/pages/af-input-fee/InputTransportationFeeHistoryPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        name: 'af-output-fees',
        path: 'af-output-fees',
        children: [
          {
            name: 'output-transportation-fee',
            path: 'output-transportation-fee',
            component: () => import('@/pages/af-output-fee/OutputTransportationFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-drop-point-fee',
            path: 'output-drop-point-fee',
            component: () => import('@/pages/af-output-fee/OutputDropPointFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-detention-fee',
            path: 'output-detention-fee',
            component: () => import('@/pages/af-output-fee/OutputDetentionFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-border-procedure-fee',
            path: 'output-border-procedure-fee',
            component: () => import('@/pages/af-output-fee/OutputBorderProcedureFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-transloading-fee',
            path: 'output-transloading-fee',
            component: () => import('@/pages/af-output-fee/OutputTransloadingFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-overnight-fee',
            path: 'output-overnight-fee',
            component: () => import('@/pages/af-output-fee/OutputOvernightFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-disbursement-fee',
            path: 'output-disbursement-fee',
            component: () => import('@/pages/af-output-fee/OutputDisbursementFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-icd-fee',
            path: 'output-icd-fee',
            component: () => import('@/pages/af-output-fee/OutputIcdFeePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'output-transportation-fee-history',
            path: 'output-transportation-fee-history',
            component: () => import('@/pages/af-output-fee/OutputTransportationFeeHistoryPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        name: 'template-files',
        path: 'template-files',
        component: () => import('@/pages/system-file/TemplateFilePage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'work-guides',
        path: 'work-guides',
        component: () => import('@/pages/system-file/WorkGuidePage.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('@/pages/404.vue'),
  },
  {
    name: '403',
    path: '/403',
    component: () => import('@/pages/403.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem(Constant.ACCESS_TOKEN)
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (!requiresAuth) return next()

  if (!token || !isTokenValid(token)) {
    localStorage.removeItem(Constant.ACCESS_TOKEN)
    return next({ name: 'login' })
  }

  // Chặn thật (không chỉ ẩn menu) cho các route "Cấu hình hệ thống" giới hạn theo đúng username cụ thể
  // (VD: chỉ 'super_admin'). Trước đây field `usernames` chỉ dùng để ẩn mục menu ở AppSidebar/
  // NavigationRoutes.ts — user khác vẫn vào được thẳng bằng URL vì router guard không hề đọc field này.
  const allowedUsernames = to.meta.usernames as string[] | undefined
  if (allowedUsernames && !allowedUsernames.includes(useGlobalStore().username ?? '')) {
    return next('/403')
  }

  const permStore = usePermissionStore()
  if (!permStore.isLoaded && !permStore.loadFailed) {
    await permStore.loadMyPermissions()
  }

  if (!permStore.isLoaded) return next('/403')

  const resource = (to.meta.resource as string | undefined) ?? (typeof to.name === 'string' ? to.name : undefined)
  const action = (to.meta.action as string | undefined) ?? 'read'

  if (resource) {
    const allowed = permStore.can(resource, action)
    if (allowed !== undefined) return allowed ? next() : next('/403')
  }

  return next()
})

export default router
